// The decisions the usage table makes about its own rows, separated from the markup so they
// can be tested. Rendering is covered by the pixel gate, not by unit tests (plan §2.3) — so
// what lives here is only what a screenshot would NOT catch.
import type { UserUsageReport } from '@woo-ext/api'
import type { SortOrder } from '../common/SortIndicator.tsx'
import { countCell, rateCell } from './format.ts'

/**
 * Angular's `orderBy:field:reverse`.
 *
 * ⚠️ IT COMPARES NUMBERS AS NUMBERS. The table sorts on three text columns and six numeric
 * ones through the same control, and a single `String()` comparator puts 10 before 9 in every
 * count column — which reads as corrupted data rather than as a sort bug.
 */
export function sortUsers(users: readonly UserUsageReport[], order: SortOrder): UserUsageReport[] {
  const field = order.field as keyof UserUsageReport
  const sorted = [...users].sort((a, b) => {
    const left = a[field]
    const right = b[field]
    if (typeof left === 'number' && typeof right === 'number') return left - right
    return String(left ?? '').localeCompare(String(right ?? ''))
  })
  return order.reverse ? sorted.reverse() : sorted
}

/** An empty department reads "No department", not blank. */
export function departmentCell(department: string | undefined): string {
  return department !== undefined && department !== '' ? department : 'No department'
}

/**
 * ⚠️ THE TOTAL ROW IS GATED ON THERE BEING ROWS, as well as on the value being a number. The
 * server sends a populated `total` object even for an empty result, so without this an empty
 * table carries a row of zeroes underneath it that looks like real data.
 */
export function totalCountCell(rowCount: number, value: number | null | undefined): string {
  return rowCount > 0 ? countCell(value) : 'N/A'
}

export function totalRateCell(rowCount: number, value: number | null | undefined): string {
  return rowCount > 0 ? rateCell(value) : 'N/A'
}

/**
 * `ng-if="!!ctrl.users && ctrl.users.length > 0 && !!ctrl.total"` on the export row.
 * An empty report has no CSV button at all — there is nothing to export.
 */
export function showsCsvButton(rowCount: number, hasTotal: boolean): boolean {
  return rowCount > 0 && hasTotal
}
